import { useHandler } from "@redotech/react-util/hook";
import { getDateEndOfDay, getDateStartOfDay } from "@redotech/util/date";
import {
  dateToCurrentPlainDate,
  plainDateToCurrentDate,
} from "@redotech/util/temporal";
import { memo, useMemo } from "react";
import { RedoButtonSize } from "../buttons/redo-button";
import { RedoDateRangePicker } from "./redo-date-range-picker";
import { RedoSingleDatePicker } from "./redo-single-date-picker";

export interface RedoDatePickerProps {
  date: Date | undefined;
  disabled?: boolean;
  setDate: (date: Date) => void;
  buttonSize?: RedoButtonSize;
  minDate?: Date;
  maxDate?: Date;
}

/**
 * @deprecated prefer RedoSingleDatePicker which uses Temporal
 */
export const DeprecatedDatePicker = memo(function DeprecatedDatePicker({
  date,
  setDate,
  minDate,
  maxDate,
  ...rest
}: RedoDatePickerProps) {
  const plainDate = date ? dateToCurrentPlainDate(date) : undefined;

  const plainMinDate = minDate ? dateToCurrentPlainDate(minDate) : undefined;
  const plainMaxDate = maxDate ? dateToCurrentPlainDate(maxDate) : undefined;

  const handleSetDate = useHandler((value: Temporal.PlainDate | null) => {
    if (!value) {
      return;
    }
    setDate(getDateStartOfDay(plainDateToCurrentDate(value)));
  });

  return (
    <RedoSingleDatePicker
      {...rest}
      date={plainDate}
      maxDate={plainMaxDate}
      minDate={plainMinDate}
      setDate={handleSetDate}
    />
  );
});

export interface RedoDateRangePickerProps {
  dateRange: [Date, Date] | undefined;
  setDateRange: (dateRange: [Date, Date]) => void;
  buttonSize?: RedoButtonSize;
  minDate?: Date;
  maxDate?: Date;
}

/**
 * @deprecated prefer RedoDateRangePicker which uses Temporal
 */
export const DeprecatedDateRangePicker = memo(
  function DeprecatedDateRangePicker({
    dateRange,
    setDateRange,
    buttonSize,
    minDate,
    maxDate,
  }: RedoDateRangePickerProps) {
    const plainDateRange: [Temporal.PlainDate, Temporal.PlainDate] | undefined =
      useMemo(() => {
        if (dateRange) {
          return [
            dateToCurrentPlainDate(dateRange[0]),
            dateToCurrentPlainDate(dateRange[1]),
          ];
        }
        return undefined;
      }, [dateRange]);

    const plainMinDate = minDate ? dateToCurrentPlainDate(minDate) : undefined;
    const plainMaxDate = maxDate ? dateToCurrentPlainDate(maxDate) : undefined;

    const handleSetDateRange = useHandler(
      (
        value:
          | [Temporal.PlainDate | null, Temporal.PlainDate | null]
          | undefined,
      ) => {
        if (!value) {
          return;
        }
        if (!value[0] || !value[1]) {
          return;
        }
        setDateRange([
          getDateStartOfDay(plainDateToCurrentDate(value[0])),
          getDateEndOfDay(plainDateToCurrentDate(value[1])),
        ]);
      },
    );

    return (
      <RedoDateRangePicker
        dateRange={plainDateRange}
        maxDate={plainMaxDate}
        minDate={plainMinDate}
        setDateRange={handleSetDateRange}
        size={buttonSize}
      />
    );
  },
);
